import { useState } from 'react';
import { sourcing } from '../api/endpoints';
import type { TechnicalCase, User } from '../api/types';
import { Card, Column, DataTable, ErrorBox, fmtDate, label, PageHeader, shortId, StatusBadge } from '../components/ui';
import { useAsync } from '../hooks';

const STATUS_FILTERS = [
  { value: '', title: 'Все' },
  { value: 'open', title: 'Открытые' },
  { value: 'escalated', title: 'Эскалированные' },
  { value: 'resolved', title: 'Решённые' },
];

/** Актуальная revision кейса с сервера. Строка таблицы замыкает объект из
    последнего отрендеренного ответа списка: после предыдущего действия
    (accept/assign) клик по той же строке может произойти до коммита
    ререндера → команда уйдёт со stale revision → 409 «Конфликт версий».
    Перед командой перечитываем кейс; при недоступности GET — fallback на
    revision из строки (поведение как раньше). */
async function freshCaseRevision(id: string, fallback?: number): Promise<number | undefined> {
  try {
    const fresh = await sourcing.technicalCase(id);
    return typeof fresh.revision === 'number' ? fresh.revision : fallback;
  } catch {
    return fallback;
  }
}

function AssignControl({ technicalCase, users, onDone, onError }: {
  technicalCase: TechnicalCase;
  users: User[];
  onDone: () => void;
  onError: (error: unknown) => void;
}) {
  const [assigneeId, setAssigneeId] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    onError(null);
    try {
      const revision = await freshCaseRevision(technicalCase.id, technicalCase.revision);
      await sourcing.assignCase(technicalCase.id, assigneeId, revision);
      onDone();
    } catch (x) {
      onError(x);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} aria-label="Исполнитель">
        <option value="">Исполнитель…</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.full_name ?? u.username}</option>)}
      </select>
      <button className="btn small" disabled={busy || !assigneeId} onClick={submit}>
        Назначить
      </button>
    </span>
  );
}

export function TechQueuePage() {
  const [status, setStatus] = useState('open');
  const [actionErr, setActionErr] = useState<unknown>(null);

  const list = useAsync<TechnicalCase[]>(
    () => sourcing.technicalCases(status ? { status } : undefined),
    [status],
  );
  const assignable = useAsync<User[]>(() => sourcing.assignableUsers().catch(() => [] as User[]), []);

  const run = async (id: string, action: 'accept' | 'resolve' | 'escalate', revision?: number) => {
    setActionErr(null);
    const body: Record<string, unknown> = {};
    if (action === 'escalate') {
      const reason = window.prompt('Причина эскалации:', '');
      if (reason === null) return;
      body.reason = reason;
    }
    if (action === 'resolve') {
      const answer = window.prompt('Резолюция / ответ на вопрос:', '');
      if (answer === null) return;
      body.answer = answer;
    }
    try {
      const rev = await freshCaseRevision(id, revision);
      await sourcing.caseAction(id, action, body, rev);
    } catch (x) { setActionErr(x); }
    // Перечитываем очередь и при ошибке: иначе после 409 «Устаревшая
    // версия объекта» строка навсегда остаётся со stale revision и любой
    // повторный клик снова даёт 409 (очередь не самовосстанавливается).
    finally { list.reload(); }
  };

  const columns: Column<TechnicalCase>[] = [
    { key: 'question', title: 'Вопрос', render: (r) => r.question || '—' },
    { key: 'option', title: 'Вариант', render: (r) => shortId(r.option) },
    { key: 'status', title: 'Статус', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'cycle', title: 'Цикл', render: (r) => String(r.cycle ?? '—') },
    { key: 'assignee', title: 'Исполнитель', render: (r) => (r.assignee != null ? String(r.assignee) : '—') },
    { key: 'escalated_to', title: 'Эскалирован на', render: (r) => (r.escalated_to != null ? String(r.escalated_to) : '—') },
    { key: 'created_at', title: 'Создан', render: (r) => fmtDate(r.created_at) },
    {
      key: 'actions', title: 'Действия', render: (r) => (
        r.status === 'resolved' ? null : (
          <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            {!r.assignee && <AssignControl technicalCase={r} users={assignable.data ?? []} onDone={list.reload}
              onError={(x) => { setActionErr(x); list.reload(); }} />}
            {r.assignee && <button className="btn small" onClick={() => void run(r.id, 'accept', r.revision)}>Принять</button>}
            <button className="btn small" onClick={() => void run(r.id, 'resolve', r.revision)}>Завершить</button>
            <button className="btn small" onClick={() => void run(r.id, 'escalate', r.revision)}>Эскалировать</button>
          </span>
        )
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Очередь техотдела" />
      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.title}</option>)}
        </select>
        <button className="btn" onClick={list.reload}>Обновить</button>
        <span className="muted">
          Регистрация запросов поставщикам и котировок выполняется в карточке заявки, вкладка «Расчёты».
        </span>
      </div>
      <Card>
        <ErrorBox error={actionErr} />
        <ErrorBox error={list.error} onRetry={list.reload} />
        <DataTable columns={columns} rows={list.data ?? []} empty={status ? `Нет кейсов со статусом «${label(status)}»` : 'Очередь пуста'} />
      </Card>
    </div>
  );
}
